import { Drawer, Text } from '@mantine/core'
import { CodeHighlight } from '@mantine/code-highlight'
import { TPacketList, TPacketListItem } from 'api/packets/getLoraPacketList'
import { CodeBox } from 'components/common'
import {
  RefetchButton,
  RefetchButtonProps,
} from 'components/common/RefetchButton'
import Datatable, { TDatatable, TableLink } from 'components/datatable'
import { DatatableProps } from 'components/datatable/Datatable'
import { DatatableFilter } from 'components/datatable/DatatableFilter'
import { r } from 'config/routes'
import { TFunction } from 'i18next'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatEUI } from 'shared/utils'
import { drawerStyles } from 'utils/drawerStyles'
import { getDeviceRoute } from 'views/panel/DeviceGroups/Single/getDeviceRoute'

const headers: (
  t: TFunction,
) => TDatatable<TPacketList['items'][number]>[] = t => [
  {
    id: 'time',
    label: t('generic.time'),
    colSize: 2,
    selector: (item, { dayjs }) =>
      item.time ? (
        <Text>{dayjs(item.time).format('DD.MM, HH:mm:ss')}</Text>
      ) : (
        '-'
      ),
  },
  {
    id: 'mType',
    label: 'Message type',
    colSize: 3,
    selector: item => <Text>{item?.packet?.mType && '-'}</Text>,
  },
  {
    id: 'devAddr',
    label: 'DevAddr',
    colSize: 1,
    selector: item =>
      item.devAdr ? <CodeBox value={formatEUI(item.devAdr)} /> : '-',
  },
  {
    id: 'data',
    label: t('generic.data'),
    colSize: 4,
    selector: item => (
      <Text style={{ fontFamily: 'monospace' }} truncate>
        {item?.data && '-'}
      </Text>
    ),
  },
  {
    id: 'lsnr',
    label: 'LSNR',
    colSize: 1,
    selector: item => <Text>{item.lsnr}</Text>,
  },
  {
    id: 'rssi',
    label: 'RSSI',
    colSize: 1,
    selector: item => <Text>{item.rssi}</Text>,
  },
  {
    id: 'gatewayEU',
    label: 'Gateway',
    colSize: 1,
    selector: item =>
      item?.gatewayId ? (
        <TableLink href={r['gateways.view'](item.gatewayId, 'packets')}>
          {item?.gatewayName}
        </TableLink>
      ) : (
        '-'
      ),
  },
  {
    id: 'device',
    label: 'Device',
    colSize: 2,
    selector: item =>
      item.device ? (
        <TableLink
          href={getDeviceRoute(item.device.type)(item.device.deviceId)}
        >
          {item.device.name}
        </TableLink>
      ) : (
        '-'
      ),
  },
  {
    id: 'freq',
    label: 'Freq',
    colSize: 1,
    selector: item => <Text>{item.freq}</Text>,
  },
  {
    id: 'datr',
    label: 'Datr',
    colSize: 1,
    selector: item => <Text>{item.datr}</Text>,
  },
  {
    id: 'codr',
    label: 'Codr',
    colSize: 1,
    selector: item => <Text>{item.codr}</Text>,
  },
  {
    id: 'fCnt',
    label: 'fCnt',
    colSize: 1,
    selector: item => <Text>{item.fCnt}</Text>,
  },
]

export const LoraPacketTable = ({
  data,
  noShadow = true,
  dataUpdatedAt,
  isFetching,
  refetch,
  leftSection,
  ...props
}: { data: TPacketList['items']; leftSection?: ReactNode } & Pick<
  DatatableProps<TPacketListItem>,
  'noShadow'
> &
  RefetchButtonProps) => {
  const { t } = useTranslation()
  const [drawer, setDrawer] = useState<TPacketListItem | null>(null)

  return (
    <>
      <DatatableFilter
        datatableId={`loraPacketTable`}
        headers={headers(t)}
        initialPreset={[
          'rssi',
          'lsnr',
          'time',
          'freq',
          'device',
          'datr',
          'codr',
          'mType',
        ]}
        leftSection={leftSection}
        rightSection={
          <RefetchButton
            refetch={refetch}
            dataUpdatedAt={dataUpdatedAt}
            isFetching={isFetching}
          />
        }
      >
        {filteredHeaders => (
          <Datatable
            headers={filteredHeaders}
            data={data}
            onClick={item => setDrawer(item)}
            noShadow={noShadow}
            {...props}
          />
        )}
      </DatatableFilter>
      <Drawer
        padding={'lg'}
        styles={drawerStyles}
        position="right"
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        title={t('packet.eventDetails')}
      >
        <CodeHighlight
          withLineNumbers
          language="json"
          copyLabel={t('generic.copy')!}
          copiedLabel={t('generic.copied')!}
          code={JSON.stringify(drawer, null, 2)}
        />
      </Drawer>
    </>
  )
}
